import React, { useState, useEffect } from 'react';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import RouteHero from './components/RouteHero';
import VehicleOptions from './components/VehicleOptions';
import RouteInformation from './components/RouteInformation';
import BookingForm from './components/BookingForm';
import CustomerReviews from './components/CustomerReviews';
import SafetyFeatures from './components/SafetyFeatures';
import PopularRoutes from './components/PopularRoutes';

const RouteDetails = () => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const routeData = {
    from: "Amritsar",
    to: "Delhi",
    distance: "450 km",
    duration: "7-8 hours",
    rating: 4.8,
    startingPrice: 8500
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TravelAction",
    "name": `${routeData?.from} to ${routeData?.to} Taxi Service`,
    "description": `Book taxi from ${routeData?.from} to ${routeData?.to}. Distance: ${routeData?.distance}, Duration: ${routeData?.duration}. Starting at ₹${routeData?.startingPrice}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Advait Tour and Travels",
      "telephone": "+91-79986-66660"
    },
    "fromLocation": {
      "@type": "Place",
      "name": routeData?.from
    },
    "toLocation": {
      "@type": "Place",
      "name": routeData?.to
    }
  };

  const vehicles = [
  {
    id: 1,
    name: "Sedan",
    type: "Comfortable & Economical",
    capacity: 4,
    luggage: 2,
    price: 8500,
    originalPrice: 10000,
    discount: 15,
    popular: true,
    image: "https://images.unsplash.com/photo-1718199553902-e10459ed04ff",
    imageAlt: "White sedan car parked on urban street with modern buildings in background during daytime",
    features: ["AC", "Music System", "GPS"]
  },
  {
    id: 2,
    name: "SUV",
    type: "Spacious & Premium",
    capacity: 6,
    luggage: 4,
    price: 12500,
    image: "https://images.unsplash.com/photo-1711466032798-efe06a566d53",
    imageAlt: "Black luxury SUV vehicle parked on mountain road with scenic valley view in background",
    features: ["AC", "Music System", "GPS", "Extra Space"]
  },
  {
    id: 3,
    name: "Tempo Traveller",
    type: "Group Travel Solution",
    capacity: 12,
    luggage: 8,
    price: 18500,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1aa88d0b3-1766989338775.png",
    imageAlt: "White tempo traveller minibus parked at tourist destination with group of travelers standing nearby",
    features: ["AC", "Music System", "GPS", "Push Back Seats"]
  }];


  const routeInfo = {
    overview: "The Amritsar to Delhi route is one of the most popular travel corridors in North India, connecting the spiritual heart of Punjab with the national capital. This journey takes you through the vibrant landscapes of Punjab and Haryana, offering a perfect blend of cultural heritage and modern infrastructure. The route is well-maintained with excellent highway conditions, making it ideal for comfortable road travel.",
    highlights: [
    "Well-maintained NH44 highway with minimal traffic",
    "Multiple rest stops and restaurants along the route",
    "Scenic views of Punjab and Haryana countryside",
    "Safe and secure travel with 24/7 support",
    "Flexible pickup and drop locations"],

    importantInfo: [
    "Toll charges included in the fare",
    "Driver allowance covered",
    "Fuel costs included",
    "Free waiting time up to 30 minutes",
    "24/7 customer support available"],

    stops: [
    {
      name: "Jalandhar",
      description: "Major city in Punjab, perfect for breakfast stop with numerous restaurants and cafes",
      duration: "30 mins",
      distance: "85 km from Amritsar"
    },
    {
      name: "Ludhiana",
      description: "Industrial hub of Punjab, good for lunch break with shopping options",
      duration: "45 mins",
      distance: "145 km from Amritsar"
    },
    {
      name: "Ambala",
      description: "Border city between Punjab and Haryana, popular rest stop",
      duration: "20 mins",
      distance: "280 km from Amritsar"
    },
    {
      name: "Karnal",
      description: "Famous for its dhabas and local cuisine, last major stop before Delhi",
      duration: "30 mins",
      distance: "380 km from Amritsar"
    }],

    pricingDetails: [
    {
      icon: "Car",
      label: "Base Fare",
      description: "Vehicle rental for the journey",
      amount: 6500
    },
    {
      icon: "Fuel",
      label: "Fuel Charges",
      description: "Estimated fuel cost for 450 km",
      amount: 1500
    },
    {
      icon: "Coins",
      label: "Toll Charges",
      description: "Highway toll fees",
      amount: 350
    },
    {
      icon: "User",
      label: "Driver Allowance",
      description: "Driver charges for the trip",
      amount: 150
    }],

    totalCost: 8500,
    travelTips: [
    {
      icon: "Clock",
      title: "Best Travel Time",
      description: "Early morning (5-7 AM) to avoid traffic and reach Delhi by afternoon"
    },
    {
      icon: "CloudRain",
      title: "Weather Considerations",
      description: "Check weather forecast, especially during monsoon (July-September)"
    },
    {
      icon: "Coffee",
      title: "Food Stops",
      description: "Try authentic Punjabi dhabas in Murthal and Karnal for delicious food"
    },
    {
      icon: "Smartphone",
      title: "Stay Connected",
      description: "Keep your phone charged and share your trip details with family"
    },
    {
      icon: "Briefcase",
      title: "Pack Smart",
      description: "Carry water, snacks, and essential medicines for the journey"
    },
    {
      icon: "MapPin",
      title: "Route Planning",
      description: "NH44 is the fastest route, avoid city roads during peak hours"
    }]

  };

  const reviews = [
  {
    id: 1,
    name: "Rajesh Kumar",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_162f7d819-1763295612623.png",
    avatarAlt: "Professional headshot of Indian man with short black hair wearing blue formal shirt smiling at camera",
    rating: 5,
    date: "2 days ago",
    comment: "Excellent service! The driver was punctual, professional, and the car was very clean. Made multiple stops as requested without any issues. Highly recommend Advait Tour and Travels for this route."
  },
  {
    id: 2,
    name: "Priya Sharma",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_111e29bbe-1763294966477.png",
    avatarAlt: "Professional headshot of Indian woman with long black hair wearing traditional red kurta with warm smile",
    rating: 5,
    date: "1 week ago",
    comment: "Very comfortable journey from Amritsar to Delhi. The vehicle was spacious and well-maintained. Driver was courteous and knew the route very well. Great value for money!"
  },
  {
    id: 3,
    name: "Harpreet Singh",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f39d6cda-1763295757879.png",
    avatarAlt: "Professional headshot of Sikh man with turban and beard wearing white kurta pajama with confident expression",
    rating: 4,
    date: "2 weeks ago",
    comment: "Good experience overall. The booking process was smooth and the driver arrived on time. Only suggestion would be to provide more information about rest stops beforehand."
  },
  {
    id: 4,
    name: "Anjali Verma",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_19be4cc4f-1763296487049.png",
    avatarAlt: "Professional headshot of young Indian woman with shoulder length hair wearing green salwar kameez with friendly smile",
    rating: 5,
    date: "3 weeks ago",
    comment: "Traveled with family and had a wonderful experience. The tempo traveller was perfect for our group. Driver was very helpful and made sure everyone was comfortable throughout the journey."
  }];


  const safetyFeatures = [
  {
    icon: "UserCheck",
    title: "Verified Drivers",
    description: "All drivers are background verified with valid licenses and years of experience"
  },
  {
    icon: "Shield",
    title: "Vehicle Safety",
    description: "Regular maintenance checks and safety inspections for all vehicles"
  },
  {
    icon: "Phone",
    title: "24/7 Support",
    description: "Round-the-clock customer support for any assistance during your journey"
  },
  {
    icon: "MapPin",
    title: "GPS Tracking",
    description: "Real-time vehicle tracking for enhanced safety and peace of mind"
  }];


  const popularRoutes = [
  {
    from: "Chandigarh",
    to: "Manali",
    distance: "310 km",
    duration: "8-9 hours",
    startingPrice: 9500
  },
  {
    from: "Delhi",
    to: "Shimla",
    distance: "350 km",
    duration: "7-8 hours",
    startingPrice: 8000
  },
  {
    from: "Ludhiana",
    to: "Dharamshala",
    distance: "280 km",
    duration: "6-7 hours",
    startingPrice: 7500
  },
  {
    from: "Jalandhar",
    to: "Haridwar",
    distance: "380 km",
    duration: "8-9 hours",
    startingPrice: 9000
  },
  {
    from: "Amritsar",
    to: "Chandigarh",
    distance: "230 km",
    duration: "4-5 hours",
    startingPrice: 6500
  },
  {
    from: "Delhi",
    to: "Rishikesh",
    distance: "240 km",
    duration: "5-6 hours",
    startingPrice: 7000
  }];


  const handleBookNow = () => {
    setShowBookingForm(true);
    setTimeout(() => {
      document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSelectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowBookingForm(true);
    setTimeout(() => {
      document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <>
      <SEO
        title={`${routeData?.from} to ${routeData?.to} Taxi Service | Book Cab Online - Advait Tour and Travels`}
        description={`Book taxi from ${routeData?.from} to ${routeData?.to} with Advait Tour and Travels. Distance: ${routeData?.distance}, Duration: ${routeData?.duration}. Starting at ₹${routeData?.startingPrice}. Safe, comfortable, and affordable cab booking.`}
        keywords={`${routeData?.from} to ${routeData?.to} taxi, ${routeData?.from} ${routeData?.to} cab booking, taxi fare ${routeData?.from} ${routeData?.to}, outstation taxi, intercity cab service`}
        ogTitle={`${routeData?.from} to ${routeData?.to} Taxi Service | Book Cab Online - Advait Tour and Travels`}
        ogDescription={`Book taxi from ${routeData?.from} to ${routeData?.to} with Advait Tour and Travels. Distance: ${routeData?.distance}, Duration: ${routeData?.duration}. Starting at ₹${routeData?.startingPrice}. Safe, comfortable, and affordable cab booking.`}
        ogUrl={typeof window !== 'undefined' ? window.location?.href : ''}
        canonicalUrl={typeof window !== 'undefined' ? window.location?.href : ''}
        structuredData={structuredData} />

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16 lg:pt-20">
          <RouteHero route={routeData} onBookNow={handleBookNow} />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 lg:py-16 space-y-12 md:space-y-16 lg:space-y-20">
            <VehicleOptions vehicles={vehicles} onSelectVehicle={handleSelectVehicle} />
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12" id="booking-section">
              <div className="lg:col-span-2 space-y-8 md:space-y-12">
                <RouteInformation routeInfo={routeInfo} />
                <CustomerReviews reviews={reviews} />
              </div>
              
              <div className="lg:col-span-1">
                {showBookingForm &&
                <BookingForm route={routeData} selectedVehicle={selectedVehicle} />
                }
              </div>
            </div>
            
            <SafetyFeatures features={safetyFeatures} />
            
            <PopularRoutes routes={popularRoutes} currentRoute={routeData} />
          </div>
        </main>
        
        <Footer />
      </div>
    </>);


};

export default RouteDetails;